import type { CSSProperties } from 'react'

import { faceArt } from './faces'
import { useFaceContext } from './FaceContext'
import { presence } from './shape'

export interface FaceProps {
  /** The agent or human this face belongs to. Drives the placeholder. */
  name: string
  size?: number
  online?: boolean
  /**
   * This agent's `avatar_key`. Left out, it is looked up by name in the face
   * context — which is how the existing call sites keep working without
   * passing anything new.
   */
  avatarKey?: string | null
  /** A portrait to draw whatever the key and style say. Humans use this. */
  src?: string | null
  /** Ring colour. Humans get a blue ring so they read as people at a glance. */
  ring?: string
  onClick?: () => void
}

/**
 * An avatar.
 *
 * Round, and grey patterns rather than coloured initials: colour carries
 * meaning in this system and a roomful of coloured circles would drown the
 * single accent.
 */
export function Face({ name, size = 34, online = true, avatarKey, src, ring, onClick }: FaceProps) {
  const { style: faceStyle, faces, set } = useFaceContext()
  const key = avatarKey !== undefined ? avatarKey : faces[name] ?? null
  const art = src
    // An uploaded portrait is nobody from the pool, so it carries no figure.
    ? ({ kind: 'image', src, figure: undefined } as const)
    : faceArt(name, key, faceStyle, { online, ring }, set)

  const style: CSSProperties = {
    width: size,
    height: size,
    flex: 'none',
    cursor: onClick ? 'pointer' : undefined,
  }

  // The agent's name first: in a room that is what you need. Who the face is
  // follows it, never replaces it. A face with no figure is an uploaded
  // portrait, which is nobody from the pool and so has no name to give.
  const label = art.figure ? `${name} · ${art.figure}` : name

  if (art.kind === 'image') {
    return (
      <img
        src={art.src}
        alt={name}
        title={label}
        onClick={onClick}
        data-online={online ? 'yes' : 'no'}
        style={{
          ...style,
          objectFit: 'cover',
          // The same silhouette as the placeholder it replaces, so an agent
          // does not change shape the moment it is given a face.
          borderRadius: 'var(--radius-face)',
          ...presence({ online, ring }),
        }}
      />
    )
  }

  return (
    <div
      role="img"
      aria-label={name}
      title={label}
      onClick={onClick}
      data-online={online ? 'yes' : 'no'}
      style={{ ...style, ...art.appearance }}
    />
  )
}
